import Button from "@material-ui/core/Button"
import Divider from "@material-ui/core/Divider"
import { makeStyles } from "@material-ui/core/styles"
import Tooltip from "@material-ui/core/Tooltip"
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import clsx from 'clsx'
import { usePopupVoucherContext } from 'context/PopupVoucher'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { formatCurrency } from 'utils/FormatNumber'
import useDetailPriceStates, { useDetailPriceActions } from 'zustand-lib/useProductDetailPrices'

interface IDiscountInfo {
  discount: number;
  index: number;
  key: string;
  subTitle: string;
  title: string;
  link?: string;
  voucherCode: string;
}

interface IProps {
  children: React.ReactElement;
  discountInfos: IDiscountInfo[];
  originPrice: number;
  tempPrice: string;
  isOnlyDiscountWithoutValue?: boolean
}

const tooltipStyles = makeStyles({
  container: {
    backgroundColor: '#fff',
    padding: 16,
    minWidth: 384,
    boxShadow: '0px 0px 30px 0px #0000001F',
    borderRadius: 10,
    margin: 0,
    zIndex: 1300,
  },
  poper: {
    pointerEvents: 'auto',
  },
});

const tooltipHeader = 'Chi tiết giá';

const ProductDetailPrices = (props: IProps) => {
  const classes = tooltipStyles();
  const { children, originPrice, discountInfos, tempPrice, isOnlyDiscountWithoutValue = false } = props;
  const { isOpenTooltip } = useDetailPriceStates();
  const { toggleDialog, toggleTooltip } = useDetailPriceActions();

  const router = useRouter();

  useEffect(() => {
    // clear toggle after route change
    router.events.on('routeChangeStart', () => {
      toggleDialog(false);
      toggleTooltip(false);
    });

    return () => {
      router.events.off('routeChangeStart', () => {
        toggleDialog(false);
        toggleTooltip(false);
      });
    };
  }, [router.events]);

  // If don't have discountInfo, just render children
  if (!discountInfos?.length || isOnlyDiscountWithoutValue) {
    return children;
  }

  return (
    <div>
      <Tooltip
        open={isOpenTooltip}
        title={<DetailTooltip originPrice={originPrice} discountInfos={discountInfos} tempPrice={tempPrice} />}
        classes={{
          tooltip: classes.container,
          popper: classes.poper,
        }}
      >
        <div
          className="cursor-pointer"
          onMouseOver={() => toggleTooltip(true)}
          onFocus={() => toggleTooltip(true)}
          onMouseOut={() => toggleTooltip(false)}
          onBlur={() => toggleTooltip(false)}
        >
          {children}
        </div>
      </Tooltip>
    </div>
  );
};

export default ProductDetailPrices;

interface IDetailTooltip {
  originPrice: number;
  discountInfos: IDiscountInfo[];
  tempPrice: string;
}

const DetailTooltip = (props: IDetailTooltip) => {
  const { originPrice = 0, tempPrice = '', discountInfos } = props;
  const { toggleTooltip } = useDetailPriceActions();

  // Find DEAL discount to calculate VAT invoice price
  const dealDiscount = (discountInfos || [])?.find((discount) => discount?.key === 'DEAL');
  // If DEAL exists: vatInvoicePrice = originPrice - deal
  // If no DEAL: vatInvoicePrice = originPrice
  const vatInvoicePrice = dealDiscount ? originPrice - dealDiscount?.discount : originPrice;

  return (
    <div className="text-black flex flex-col gap-[12px] z-50" onMouseEnter={() => toggleTooltip(true)} onMouseLeave={() => toggleTooltip(false)}>
      <div className="text-[16px] font-ggsm">{tooltipHeader}</div>
      <VoucherWithDiscount label="Giá gốc" discount={formatCurrency(originPrice)} discountClasses="font-ggsm !text-black" />

      {/* Display DEAL if exists */}
      {dealDiscount && (
        <VoucherWithDiscount
          label={dealDiscount.title}
          discount={`-${formatCurrency(dealDiscount.discount)}`}
          subLabel={dealDiscount.subTitle}
          labelClasses="font-ggsm"
          link={dealDiscount?.link}
          voucherCode={dealDiscount?.voucherCode}
        />
      )}

      {/* Display VAT invoice price after origin price (or after DEAL if exists) */}
      {vatInvoicePrice > 0 && (
        <>
          <VoucherWithDiscount
            label="Giá sau giảm giá trực tiếp"
            discount={formatCurrency(vatInvoicePrice)}
            labelClasses="font-ggsm"
            discountClasses="font-ggsm !text-black"
          />
          <Divider />
        </>
      )}

      {/* Display other discounts (excluding DEAL) */}
      {(discountInfos || [])
        .filter((discount) => discount?.key !== 'DEAL')
        .map((discount, index) => (
          <VoucherWithDiscount
            key={discount.key || index}
            label={discount.title}
            discount={`-${formatCurrency(discount.discount)}`}
            subLabel={discount.subTitle}
            labelClasses="font-ggsm"
            link={discount?.link}
            voucherCode={discount?.voucherCode}
          />
        ))}

      <Divider />
      <VoucherWithDiscount
        label="Giá tạm tính <br/> (khi áp dụng các mã giảm giá trên)"
        discount={tempPrice}
        discountClasses="!text-[#15A959] font-ggsm !my-0"
      />
    </div>
  );
};

interface IVoucherWithDiscount {
  label: string;
  discount: string;
  subLabel?: string;
  labelClasses?: string;
  discountClasses?: string;
  link?: string;
  voucherCode?: string;
}

const VoucherWithDiscount = (props: IVoucherWithDiscount) => {
  const { label, discount, subLabel, voucherCode = '', labelClasses, discountClasses, link = '' } = props;
  const { setTargetVoucher, toggleTooltip } = useDetailPriceActions();

  const { handleOpen } = usePopupVoucherContext();

  const handleClick = () => {
    toggleTooltip(false);
    setTargetVoucher(voucherCode);
    handleOpen(voucherCode);
  };

  return (
    <div className="grid grid-cols-3">
      <div className="col-span-2 flex flex-col gap-[4px]">
        <div className={clsx('text-[14px] leading-5', labelClasses)}>
          {label?.split('<br/>').map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </div>
        {subLabel ? <div className="text-[12px] text-[#8C8C8C]">{subLabel}</div> : null}
        {voucherCode ? (
          <Button className="max-w-max p-0" onClick={handleClick}>
            <div className="text-sm flex items-center text-[#1D39C4] ">
              Xem chi tiết&nbsp;&nbsp;
              <ErrorOutlineIcon className="text-[#1D39C4] !w-[12px] !h-[12px]" />
            </div>
          </Button>
        ) : null}
      </div>
      <div className={clsx('col-span-1 text-right text-[#F5222D] text-[14px] my-auto leading-[20px]', discountClasses)}>{discount}</div>
    </div>
  );
};
